<template>
  <div>
    <div v-if="!loader">
      <b-card class="row-height" no-body>
        <div>
          <b-card no-body class="card-statistics" style="">
            <b-card-body class="statistics-body">
              <h2 class="text-purple font-weight-bold mb-1">
                {{ $t("referrer_details") }}: {{ referrerCompanyName }}
              </h2>
              <br>
              <div>
                <h4 class="text-purple font-weight-bold mb-1">
                  {{ $t("referrals") }}
                </h4>
                <b-table hover :items="parsedCompanies" :fields="companyFields" responsive :key="tableKey">
                  <template #head()="scope">
                    <div class="text-nowrap">{{ $t(scope.label) }}</div>
                  </template>
                  <template #cell(company_id)="data">
                    <span v-if="data.item.company_id">C-{{ data.item.company_id }}</span>
                    <span v-else>-</span>
                  </template>
                  <template #cell(company_name)="data">
                    <span v-if="data.item.company_name">{{ data.item.company_name }}</span>
                    <span v-else-if="data.item.main_contact_name">{{ data.item.main_contact_name }}</span>
                    <span v-else>-</span>
                  </template>
                  <template #cell(invoices_sold)="data">
                    <span v-if="data.item.invoices_sold">{{ data.item.invoices_sold }}</span>
                    <span v-else>0</span>
                  </template>
                </b-table>
                <br>
                <h4 class="text-purple font-weight-bold mb-1">
                  {{ $t("transactions") }}
                </h4>
                <b-table
                  hover
                  :items="allTransactions"
                  :fields="transactionFields"
                  responsive
                  class="mt-2"
                >
                  <template #head()="scope">
                    <div class="text-nowrap">{{ $t(scope.label) }}</div>
                  </template>

                  <template #cell(transaction_id)="data">
                    <span v-if="data.item.id">T-{{ data.item.id }}</span>
                    <span v-else>-</span>
                  </template>

                  <template #cell(company_id)="data">
                    <span v-if="data.item.company_id">C-{{ data.item.company_id }}</span>
                    <span v-else>-</span>
                  </template>

                  <template #cell(invoice_id)="data">
                    <span v-if="data.item.invoice_id">I-{{ data.item.invoice_id }}</span>
                    <span v-else>-</span>
                  </template>

                  <template #cell(amount)="data">
                    <span v-if="data.item.amount">{{ amountFormat(data.item.amount, data.item.currency.name ) }}</span>
                    <span v-else>0.00</span>
                  </template>

                  <template #cell(aequitex_fee)="data">
                    <span v-if="data.item.aequitex_fee">{{ amountFormat(data.item.aequitex_fee, data.item.currency.name ) }}</span>
                    <span v-else>0.00</span>
                  </template>

                  <template #cell(currency)="data">
                    <span v-if="data.item.currency">{{ data.item.currency.name }}</span>
                    <span v-else>-</span>
                  </template>

                  <template #cell(created_at)="data">
                    <span v-if="data.item.created_at">
                      {{ data.item.created_at | formatDate }}
                    </span>
                    <span v-else>-</span>
                  </template>

                  <template #cell(status)="data">
                    <span class="text-success" v-if="data.item.referral_program_status == 1">
                      {{ $t('paid') }}
                    </span>
                    <span class="text-danger" v-else-if="data.item.referral_program_status == 2">
                      {{ $t('canceled') }}
                    </span>
                    <span class="text-warning" v-else>{{ $t('pending') }}</span>
                  </template>

                  <template #cell(action)="data">
                    <b-button v-if="adminType(['super_admin']) && data.item.referral_program_status == 0"
                      size="sm"
                      class="mr-1"
                      v-b-tooltip.hover
                      :title="$t('cancel')"
                      variant="outline-danger"
                      @click="openModal('cancel', data.item)"
                    >
                      <feather-icon icon="XIcon"/>
                    </b-button>
                  </template>
                </b-table>
                <br>
                <div class="row alert alert-secondary p-2">
                  <div
                      v-for="currency in ['CHF', 'EUR', 'USD', 'GBP']"
                      :key="currency"
                      class="col text-nowrap d-flex align-items-center"
                  >
                    <strong>{{ $t("total") }} {{ currency }}: </strong>
                    <p class="ml-2 mb-0" v-if="referralProgramDetails">{{ amountFormat(getAmount(currency),currency) }}</p>
                  </div>
                </div>
              </div>
            </b-card-body>
          </b-card>
        </div>
      </b-card>
    </div>
    <div v-else><img src="/new-loader.svg"/></div>
    <ApproveModal
      :text-content="modalText"
      :title="modalTitle"
      :action="modalAction"
      @ok="handleModalConfirm"
      @close="showModal = false"
    />
  </div>
</template>
<script>
import {ValidationProvider, ValidationObserver} from "vee-validate";
import Table from "@/components/Common/Table2";
import {CurrencyAmountFormat} from '@core/utils/CurrencyAmountFormat';
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ApproveModal from "@/components/Common/ApproveModal.vue";

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    ApproveModal,
    Table
  },
  data() {
    return {
      partner: null,
      commission: null,
      member_discount: null,
      hive_fee: null,
      show_username: true,
      total: 0,
      company: null,
      billing: null,
      start_date: null,
      end_date: null,
      loader: false,
      referrerName: '',
      preregistered: false,
      referralProgramDetails: null,
      modalText: null,
      modalTitle: null,
      modalAction: null,
      modalItem: null,
      showModal: false,
      tableKey: 0,
      companyFields: [
        {label: "company_id", key: "company_id", visible: true},
        {label: "company_name", key: "company_name", visible: true},
        {label: "transactions_made", key: "invoices_sold", visible: true},
      ],
      transactionFields: [
        {label: "transaction_id", key: "transaction_id", visible: true},
        {label: "company_id", key: "company_id", visible: true, sortable: true},
        {label: "invoice_id", key: "invoice_id", visible: true, sortable: true},
        {label: "currency", key: "currency", visible: true, sortable: true},
        {label: "amount", key: "amount", visible: true, sortable: true},
        {label: "earned aequitex fee", key: "aequitex_fee", visible: true, sortable: true},
        {label: "created_at", key: "created_at", visible: true, sortable: true},
        {label: "status", key: "status", visible: true, sortable: true},
        {label: "action", key: "action", visible: true, sortable: true},
      ],
    };
  },
  computed: {
    parsedCompanies() {
      if (!this.referralProgramDetails || !this.referralProgramDetails.companies) return [];
      if (typeof this.referralProgramDetails.companies === 'string') {
        try {
          return JSON.parse(this.referralProgramDetails.companies);
        } catch (e) {
          return [];
        }
      }
      return Array.isArray(this.referralProgramDetails.companies) ? this.referralProgramDetails.companies : [];
    },
    allTransactions() {
      const transactions = this.parsedCompanies
          .filter(company => Array.isArray(company.transactions))
          .flatMap(company => company.transactions);
      return transactions;
    },
    referrerCompanyName() {
      const company = this.referralProgramDetails?.company;
      if (!company) return '—';
      if (company.name) return company.name;
      if (company.main_contact_person)
        return `${company.main_contact_person.name} ${company.main_contact_person.last_name}`;
      return '—';
    }
  },
  created() {
    this.getReferrerDetails();
  },
  methods: {
    amountFormat(amount, currency) {
      return CurrencyAmountFormat(amount, currency)
    },

    getAmount(currency) {
      let total = 0;

      this.parsedCompanies.forEach(company => {
        if (Array.isArray(company.transactions)) {
          company.transactions.forEach(tx => {
            if (tx.referral_program_status == 0 && tx.currency.name === currency) {
              total += parseFloat(tx.aequitex_fee);
            }
          });
        }
      });

      return total;
    },
    showSuccessMessage(message, type) {
      let text = this.$t(message);
      this.$swal({
        position: "center",
        icon: "success",
        title: text,
        showConfirmButton: false,
        timer: 1500,
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
      });
    },
    getReferrerDetails() {
      this.$http
      .post(`/referral-program/${this.$route.params.id}`)
      .then(response => {
        this.referralProgramDetails = response.data.value;
        this.referrerName = response.data.value?.company?.name || '—';
      })
      .catch(() => {
        this.referralProgramDetails = null;
        this.referrerName = '—';
      });
    },
    openModal(action, item) {
      this.modalAction = action
      this.modalItem = item
      if (action == 'cancel') {
        this.modalTitle = this.$t('cancel')
        this.modalText = this.$t('are_you_sure_cancel_referral')
      }
      this.$root.$bvModal.show('approveModal')
    },
    callApproveModalAction() {
      if (this.modalAction === 'cancel') {
        this.doCancel(this.modalItem)
      }
    },
    handleModalConfirm() {
      if (this.modalAction === 'cancel') {
        this.doCancel(this.modalItem)
      }
      this.showModal = false
    },
    doCancel(item) {
      this.$http
      .post(`/referral-program/${item.id}/cancel-transaction`)
      .then((res) => {
        if (res) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "success",
              icon: "SuccessIcon",
              variant: "success",
            },
          });
          this.getReferrerDetails();
          this.tableKey += 1;
        }
      });
    },
  },
};
</script>